/**
 * Der Prüfbogen – der Bildschirm, an dem Bernd draußen arbeitet.
 *
 * Aufbau: erst die 13 Platzkriterien nach DIN 18034, dann die Geräte. Jede
 * Zeile zeigt die Stufe als Zeichen, den Vorjahresstand und die Zahl der
 * Fotos. Antippen öffnet die Beurteilung als Blatt von unten.
 *
 * Alles läuft gegen den Bogen im lokalen Speicher; Änderungen gehen in die
 * Warteschlange. Ohne Empfang merkt man außer dem Hinweis „offline" nichts.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { schluessel, usePruefbogen } from '../api/abfragen'
import { ApiFehler, api } from '../api/klient'
import {
  PRUEFARTTEXT,
  type AbschlussKonflikt,
  type Geraetebefund,
  type Platzbefund,
  type Pruefbogen as PruefbogenTyp,
} from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import {
  Fehlerhinweis,
  Ladeanzeige,
  Leerhinweis,
  Stufenzeichen,
  datumDeutsch,
} from '../bausteine/Hinweise'
import { Unterschriftfeld } from '../bausteine/Unterschriftfeld'
import { darfPruefen, useKonto } from '../konto/KontoKontext'
import { auftraegeZu, einreihen } from '../offline/db'
import { abarbeiten, beobachten } from '../offline/warteschlange'
import { useBogenAendern } from '../pruefbogen/aenderungen'
import { BefundBearbeiten } from '../pruefbogen/BefundBearbeiten'
import { Tunnel } from '../pruefbogen/Tunnel'
import { Werkzeugleiste } from '../pruefbogen/Werkzeugleiste'
import { Altmaengel } from '../pruefbogen/Altmaengel'
import { Grundlagen } from '../pruefbogen/Grundlagen'
import { Fotogalerie } from '../pruefbogen/Fotogalerie'

export function Pruefbogen() {
  const { pruefungId = '' } = useParams()
  const { konto } = useKonto()
  const gehZu = useNavigate()

  const bogen = usePruefbogen(pruefungId)
  const aendern = useBogenAendern(pruefungId)
  const speicher = useQueryClient()

  /* Ein Punkt lässt sich von außen aufschlagen: /bogen?punkt=<id>.

     Gebraucht von der Warteschlange. Wurde dort etwas abgelehnt, half der
     Verweis auf den Bogen wenig – er zeigte auf die Liste, und welcher der
     dreißig Punkte gemeint war, stand nirgends. */
  const [suche] = useSearchParams()
  const [offenerBefund, setzeOffenenBefund] = useState<string | null>(
    suche.get('punkt'),
  )
  const [wartend, setzeWartend] = useState(0)
  const [abschlussOffen, setzeAbschlussOffen] = useState(false)
  // Gerätebefund, der gerade im Schrittablauf bearbeitet wird.
  const [tunnelFuer, setzeTunnelFuer] = useState<string | null>(null)
  const [fehler, setzeFehler] = useState<unknown>(null)
  /* Der zuletzt eingetragene Normverweis, für den nächsten Punkt.
     Nur im Zustand dieser Seite: nach dem Verlassen des Bogens soll
     er nicht in eine andere Prüfung hinüberwirken. */
  const [letzteNorm, setzeLetzteNorm] = useState<string | null>(null)

  /* „o.B." direkt aus der Liste, ohne den Punkt aufzuschlagen.

     Bei den meisten Prüfungen ist der weitaus größte Teil in Ordnung.
     Jeden Punkt einzeln aufzuschlagen, „o.B." zu tippen und das Blatt
     wieder zu schließen sind drei Griffe für eine Feststellung, die keine
     Eingabe braucht – mal dreißig Punkte, mit Handschuhen, auf dem Platz.

     Nur, solange der Punkt unbeurteilt ist: etwas zu überschreiben, was
     schon dasteht, darf kein einzelner Fingertipp können. Ändern geht
     weiterhin im Blatt, mit Vorjahr, Norm und Fotos im Blick. */
  const [schnellLaeuft, setzeSchnellLaeuft] = useState<string | null>(null)

  const ohneBeanstandung = useCallback(
    async (befundId: string, istGeraet: boolean) => {
      setzeSchnellLaeuft(befundId)
      setzeFehler(null)
      try {
        if (istGeraet) {
          await aendern.geraetebefundBeurteilen(befundId, { stufe: 'o.B.' })
        } else {
          await aendern.platzbefundBeurteilen(befundId, { stufe: 'o.B.' })
        }
      } catch (f) {
        setzeFehler(f)
      } finally {
        setzeSchnellLaeuft(null)
      }
    },
    [aendern],
  )

  useEffect(() => beobachten((stand) => setzeWartend(stand.offen)), [])

  // Wartende Aufträge dieser Prüfung zählen – für die Anzeige unten.
  useEffect(() => {
    void auftraegeZu(pruefungId).then((liste) => setzeWartend(liste.length))
  }, [pruefungId])

  const daten = bogen.data
  const abgeschlossen = daten?.status === 'abgeschlossen'
  const nurLesen = abgeschlossen || !darfPruefen(konto)

  const alleBefunde = useMemo(
    () =>
      daten
        ? [
            ...daten.platzbefunde.map((b) => ({ befund: b, istGeraet: false as const })),
            ...daten.geraetebefunde.map((b) => ({ befund: b, istGeraet: true as const })),
          ]
        : [],
    [daten],
  )

  const gewaehlt = alleBefunde.find((e) => e.befund.id === offenerBefund)

  /**
   * Zum nächsten unbeurteilten Punkt springen und ihn gleich öffnen.
   *
   * Der Stand kommt aus dem Abfragespeicher und nicht aus `daten`: unmittelbar
   * nach einer Beurteilung hält der Aufrufer noch den Stand von vorher in der
   * Hand, und der Sprung landete dann wieder auf demselben Befund.
   */
  const zumNaechstenOffenen = useCallback(() => {
    const frisch = speicher.getQueryData<PruefbogenTyp>(schluessel.bogen(pruefungId))
    const naechster = frisch?.fortschritt.naechster_offener_id
    if (!naechster) {
      setzeOffenenBefund(null)
      return
    }
    setzeOffenenBefund(naechster)
    // Die Zeile in den Blick holen, damit nach dem Schließen des Blattes klar
    // ist, wo man steht.
    requestAnimationFrame(() => {
      document
        .getElementById(`befund-${naechster}`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }, [pruefungId, speicher])

  if (bogen.isLoading) return <Ladeanzeige text="Lädt den Prüfbogen …" />
  if (bogen.error) return <Fehlerhinweis fehler={bogen.error} />
  if (!daten) return <Leerhinweis titel="Prüfbogen nicht gefunden" />

  return (
    // data-bereich markiert die Beurteilungsfläche. Der Stil nimmt darin
    // die orange Hausfarbe aus Knöpfen und Feldern heraus, damit neben
    // dem orangebraunen Zeichen der Mängelstufe 2 nichts anderes orange
    // leuchtet. Siehe stil.css.
    <div data-bereich="pruefbogen" className="pb-24">
      <header className="mb-4">
        <h1 className="text-2xl font-bold">{daten.spielplatz_name}</h1>
        <p className="text-slate-700">
          {daten.kunde_name} · {PRUEFARTTEXT[daten.pruefart]} · {datumDeutsch(daten.datum)}
        </p>
        {abgeschlossen && (
          <p className="mt-2 rounded-feld border-2 border-stufe-ob bg-stufe-ob-grund p-2 font-semibold text-stufe-ob">
            Abgeschlossen als {daten.berichtsnummer}. Korrekturen nur über eine Nachprüfung.
          </p>
        )}
      </header>

      <Fehlerhinweis fehler={fehler} />

      {/* Die Normen, nach denen geprüft wird. Sie stehen vor den Befunden:
          wonach geprüft wird, entscheidet sich vor dem ersten Kreuz. */}
      <div className="mb-4">
        <Grundlagen pruefung={daten} nurLesen={abgeschlossen} />
      </div>

      {/* Bei einer Nachprüfung steht das ganz oben: sie geht nicht den Platz
          durch, sondern die Mängel, die offen geblieben sind. */}
      <div className="mb-6">
        <Altmaengel
          pruefungId={daten.id}
          istNachpruefung={daten.pruefart === 'nachpruefung'}
        />
      </div>

      <section className="mb-6">
        <h2 className="ueberschrift-akzent mb-3 text-xl">Spielplatz nach DIN 18034</h2>
        <ul className="space-y-2">
          {daten.platzbefunde.map((b) => (
            <BefundZeile
              key={b.id}
              befund={b}
              zusatz={b.gruppe}
              beimOeffnen={() => setzeOffenenBefund(b.id)}
              schnellOhneBefund={
                nurLesen ? undefined : () => void ohneBeanstandung(b.id, false)
              }
              schnellLaeuft={schnellLaeuft === b.id}
            />
          ))}
        </ul>
      </section>

      <section>
        <h2 className="ueberschrift-akzent mb-3 text-xl">
          Spielgeräte ({daten.geraetebefunde.length})
        </h2>
        {daten.geraetebefunde.length === 0 ? (
          <Leerhinweis titel="Keine Geräte im Bestand" />
        ) : (
          <ul className="space-y-2">
            {daten.geraetebefunde.map((b) => (
              <BefundZeile
                key={b.id}
                befund={b}
                zusatz={[b.kennung && `ID ${b.kennung}`, b.geraetetyp]
                  .filter(Boolean)
                  .join(' · ')}
                gesperrt={b.gesperrt}
                beimOeffnen={() => setzeOffenenBefund(b.id)}
                schnellOhneBefund={
                  nurLesen ? undefined : () => void ohneBeanstandung(b.id, true)
                }
                schnellLaeuft={schnellLaeuft === b.id}
              />
            ))}
          </ul>
        )}
      </section>

      {gewaehlt && (
        <BefundBearbeiten
          befund={gewaehlt.befund}
          istGeraet={gewaehlt.istGeraet}
          pruefungId={daten.id}
          geraetetyp={
            gewaehlt.istGeraet ? (gewaehlt.befund as Geraetebefund).geraetetyp : undefined
          }
          nurLesen={nurLesen}
          letzteNorm={letzteNorm}
          normGemerkt={setzeLetzteNorm}
          beimSpeichern={async (eingabe) => {
            if (gewaehlt.istGeraet) {
              await aendern.geraetebefundBeurteilen(gewaehlt.befund.id, eingabe)
            } else {
              await aendern.platzbefundBeurteilen(gewaehlt.befund.id, eingabe)
            }
          }}
          beimZuruecksetzen={() =>
            aendern.zuruecksetzen(gewaehlt.befund.id, gewaehlt.istGeraet)
          }
          beimFoto={async (datei, art) => {
            await aendern.fotoAufnehmen(datei, gewaehlt.befund.id, art)
          }}
          bausteinVerwendet={aendern.bausteinVerwendet}
          // Nur Geräte haben einen Ablauf; Platzkriterien nicht.
          tunnelOeffnen={
            gewaehlt.istGeraet && !nurLesen
              ? () => {
                  setzeTunnelFuer(gewaehlt.befund.id)
                  setzeOffenenBefund(null)
                }
              : undefined
          }
          schliessen={() => setzeOffenenBefund(null)}
          weiterZumNaechsten={() => {
            setzeOffenenBefund(null)
            // Kurz warten, damit der neue Fortschritt schon im Speicher steht.
            setTimeout(zumNaechstenOffenen, 60)
          }}
        />
      )}

      {tunnelFuer && (
        <Tunnel
          pruefungId={pruefungId}
          befund={
            daten.geraetebefunde.find((b) => b.id === tunnelFuer) as import('../api/typen').Geraetebefund
          }
          schliessen={() => setzeTunnelFuer(null)}
          beimFoto={async (datei) => {
            await aendern.fotoAufnehmen(datei, tunnelFuer, 'detail')
          }}
          fertig={() => {
            setzeTunnelFuer(null)
            void bogen.refetch().then(() => setTimeout(zumNaechstenOffenen, 60))
          }}
        />
      )}

      {abschlussOffen && (
        <AbschlussBlatt
          bogen={daten}
          schliessen={() => setzeAbschlussOffen(false)}
          fertig={() => {
            setzeAbschlussOffen(false)
            void bogen.refetch()
          }}
        />
      )}

      <Werkzeugleiste
        fortschritt={daten.fortschritt}
        wartend={wartend}
        abgeschlossen={abgeschlossen || nurLesen}
        beimSprung={zumNaechstenOffenen}
        beimFoto={(datei) => {
          setzeFehler(null)
          // Ohne Befundbezug: eine Übersichtsaufnahme des Platzes.
          void aendern.fotoAufnehmen(datei, undefined, 'uebersicht').catch(setzeFehler)
        }}
        beimAbschluss={() => setzeAbschlussOffen(true)}
        beimZurueck={() => gehZu(`/spielplaetze/${daten.spielplatz_id}`)}
      />

      {/* Am Ende, weil sie beim Sichten vor dem Abschluss gebraucht wird –
          nicht beim Beurteilen selbst. */}
      <section className="mt-8">
        <h2 className="ueberschrift-akzent mb-3 text-xl">Fotos dieser Prüfung</h2>
        <Fotogalerie
          pruefungId={daten.id}
          abgeschlossen={abgeschlossen}
          benennen={(foto) => {
            if (foto.geraetebefund_id) {
              const b = daten.geraetebefunde.find((x) => x.id === foto.geraetebefund_id)
              return b ? b.bezeichnung : 'Gerät'
            }
            if (foto.platzbefund_id) {
              const b = daten.platzbefunde.find((x) => x.id === foto.platzbefund_id)
              return b ? b.bezeichnung : 'Platzkriterium'
            }
            return 'Zum Platz'
          }}
        />
      </section>
    </div>
  )
}

/** Eine Zeile im Bogen. */
function BefundZeile({
  befund,
  zusatz,
  gesperrt = false,
  beimOeffnen,
  schnellOhneBefund,
  schnellLaeuft = false,
}: {
  befund: Platzbefund | Geraetebefund
  zusatz?: string | null
  gesperrt?: boolean
  beimOeffnen: () => void
  /** Direkt „o.B." setzen, ohne den Punkt zu öffnen. Nur für Platzpunkte. */
  schnellOhneBefund?: () => void
  schnellLaeuft?: boolean
}) {
  const offen = befund.beurteilt_am === null

  return (
    <li id={`befund-${befund.id}`} className="flex items-stretch gap-2">
      <button
        type="button"
        onClick={beimOeffnen}
        className={`karte-knopf ${offen ? 'border-dashed border-offen' : ''}`}
      >
        <Stufenzeichen stufe={befund.stufe} />

        <div className="min-w-0 flex-1">
          <div className="font-semibold">{befund.bezeichnung}</div>
          <div className="truncate text-base text-slate-700">
            {befund.mangel ? befund.mangel : zusatz}
          </div>
          {befund.vorjahr_stufe && befund.vorjahr_stufe !== 'o.B.' && (
            <div className="text-sm text-slate-600">Vorjahr: {befund.vorjahr_stufe}</div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          {gesperrt && (
            <span className="marke border-2 border-stufe-3 text-stufe-3">gesperrt</span>
          )}
          {befund.anzahl_fotos > 0 && (
            <span className="text-sm text-slate-600">{befund.anzahl_fotos} Foto(s)</span>
          )}
        </div>
      </button>

      {/* Nur solange nichts dasteht. Ein bereits beurteilter Punkt lässt
          sich weiterhin ändern – aber im Blatt, mit allem im Blick. */}
      {schnellOhneBefund && offen && (
        <button
          type="button"
          onClick={schnellOhneBefund}
          disabled={schnellLaeuft}
          aria-label={`${befund.bezeichnung}: ohne Beanstandung`}
          className="shrink-0 rounded-feld border-2 border-stufe-ob px-4 font-bold
                     text-stufe-ob hover:bg-stufe-ob-grund active:bg-stufe-ob-grund
                     disabled:opacity-50"
          style={{ minWidth: '4.5rem' }}
        >
          {schnellLaeuft ? '…' : 'o.B.'}
        </button>
      )}
    </li>
  )
}

/**
 * Abschlussblatt.
 *
 * Zeigt vor dem Abschluss, was der Bericht aussagen wird. Sind Punkte offen,
 * benennt der Server sie – und der Abschluss geht nur weiter, wenn der Prüfer
 * ausdrücklich bestätigt, dass sie als „nicht vorhanden" gelten sollen.
 */
function AbschlussBlatt({
  bogen,
  schliessen,
  fertig,
}: {
  bogen: import('../api/typen').Pruefbogen
  schliessen: () => void
  fertig: () => void
}) {
  const [zusammenfassung, setzeZusammenfassung] = useState(bogen.zusammenfassung ?? '')
  const [naechste, setzeNaechste] = useState(() => {
    // Vorschlag: ein Jahr nach dem Prüfdatum, wie bei der Hauptuntersuchung
    // üblich. Der Prüfer kann das ändern.
    const d = new Date(bogen.datum)
    d.setFullYear(d.getFullYear() + 1)
    return d.toISOString().slice(0, 10)
  })
  const [offeneFestschreiben, setzeOffeneFestschreiben] = useState(false)
  /* Die Unterschrift für diesen Abschluss.

     Zwei Wege, beide enden hier: die hinterlegte mit einem Griff anfügen,
     oder gleich auf dem Handy unterschreiben. Der Server unterscheidet das
     nicht – er bekommt das Bild und hält fest, was unterschrieben wurde.

     Nicht verpflichtend: ein Bericht ohne Unterschrift ist keiner mit
     Fehler, sondern einer, der noch von Hand zu unterschreiben ist. */
  const { konto } = useKonto()
  const [unterschrift, setzeUnterschrift] = useState<string | null>(null)
  const [zeichnen, setzeZeichnen] = useState(false)
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)
  const [konflikt, setzeKonflikt] = useState<AbschlussKonflikt | null>(null)

  const maengel = bogen.geraetebefunde.filter(
    (b) => b.stufe === '1' || b.stufe === '2' || b.stufe === '3',
  )
  const gesperrte = bogen.geraetebefunde.filter((b) => b.gesperrt)

  async function abschliessen() {
    setzeFehler(null)
    setzeKonflikt(null)
    setzeLaeuft(true)
    try {
      await api.senden(`/api/pruefungen/${bogen.id}/abschliessen`, {
        zusammenfassung: zusammenfassung.trim() || null,
        naechste_pruefung_am: naechste || null,
        offene_als_nicht_vorhanden: offeneFestschreiben,
        unterschrift,
      })
      fertig()
    } catch (f) {
      if (f instanceof ApiFehler && f.istKonflikt) {
        const inhalt = f.detail as AbschlussKonflikt | undefined
        if (inhalt && typeof inhalt === 'object' && 'punkte_offen' in inhalt) {
          setzeKonflikt(inhalt)
        } else {
          setzeFehler(f)
        }
      } else if (f instanceof TypeError) {
        // Kein Netz: den Abschluss einreihen und später senden.
        await einreihen({
          art: 'abschluss',
          pruefung_id: bogen.id,
          pfad: `/api/pruefungen/${bogen.id}/abschliessen`,
          methode: 'POST',
          koerper: {
            zusammenfassung: zusammenfassung.trim() || null,
            naechste_pruefung_am: naechste || null,
            offene_als_nicht_vorhanden: offeneFestschreiben,
            unterschrift,
          },
          erstellt_am: Date.now(),
        })
        void abarbeiten()
        fertig()
      } else {
        setzeFehler(f)
      }
    } finally {
      setzeLaeuft(false)
    }
  }

  const fuss = (
    <>
      <div className="flex gap-2">
        <button
          type="button"
          className="knopf-haupt flex-1"
          onClick={() => void abschliessen()}
          disabled={laeuft || (konflikt !== null && !offeneFestschreiben)}
        >
          {laeuft ? 'Schließt ab …' : 'Abschließen'}
        </button>
        <button type="button" className="knopf-still px-4" onClick={schliessen}>
          Abbrechen
        </button>
      </div>
      <p className="mt-2 text-base text-slate-600">
        Nach dem Abschluss ist der Bericht unveränderlich. Korrekturen laufen über eine
        Nachprüfung.
      </p>
    </>
  )

  return (
    <Blatt
      titel="Prüfung abschließen"
      untertitel={`${bogen.spielplatz_name} · ${bogen.kunde_name}`}
      schliessen={schliessen}
      fuss={fuss}
    >
      <>
        <div className="karte mb-4 space-y-1 text-base">
          <p>
            <span className="font-semibold">{bogen.fortschritt.punkte_beurteilt}</span> von{' '}
            {bogen.fortschritt.punkte_gesamt} Punkten beurteilt
          </p>
          {maengel.length === 0 ? (
            <p className="text-stufe-ob">Keine Mängel festgestellt.</p>
          ) : (
            <ul className="list-disc pl-5">
              {maengel.map((b) => (
                <li key={b.id}>
                  Stufe {b.stufe}: {b.bezeichnung} – {b.mangel}
                </li>
              ))}
            </ul>
          )}
          {gesperrte.length > 0 && (
            <p className="font-semibold text-stufe-3">
              {gesperrte.length} {gesperrte.length === 1 ? 'Gerät' : 'Geräte'} gesperrt – den
              Kunden unverzüglich informieren.
            </p>
          )}
        </div>

        <div className="mb-3">
          <label htmlFor="zusammenfassung">Zusammenfassung für die Bescheinigung</label>
          <textarea
            id="zusammenfassung"
            value={zusammenfassung}
            onChange={(e) => setzeZusammenfassung(e.target.value)}
            placeholder="Ein Gerät gesperrt. Nachprüfung nach Mängelbeseitigung erforderlich."
          />
        </div>

        <div className="mb-3">
          <label>Unterschrift</label>
          {unterschrift ? (
            <div className="rounded-feld border-2 border-slate-200 bg-white p-2">
              <img
                src={unterschrift}
                alt="Unterschrift für diesen Bericht"
                className="max-h-20"
              />
              <button
                type="button"
                className="knopf-still mt-2 px-3 text-base"
                onClick={() => setzeUnterschrift(null)}
              >
                Entfernen
              </button>
            </div>
          ) : zeichnen ? (
            <>
              <Unterschriftfeld beimZeichnen={setzeUnterschrift} />
              <button
                type="button"
                className="knopf-still mt-2 px-3 text-base"
                onClick={() => setzeZeichnen(false)}
              >
                Abbrechen
              </button>
            </>
          ) : (
            <div className="flex flex-wrap gap-2">
              {konto?.unterschrift && (
                <button
                  type="button"
                  className="knopf-zweit px-3"
                  onClick={() => setzeUnterschrift(konto.unterschrift)}
                >
                  Meine Unterschrift anfügen
                </button>
              )}
              <button
                type="button"
                className="knopf-still px-3"
                onClick={() => setzeZeichnen(true)}
              >
                Hier unterschreiben
              </button>
            </div>
          )}
          <p className="mt-1 text-base text-slate-600">
            {konto?.unterschrift
              ? 'Ohne Unterschrift bleibt die Linie im Bericht leer.'
              : 'Unter Mein Konto lässt sich eine Unterschrift hinterlegen – dann genügt hier ein Griff.'}
          </p>
        </div>

        <div className="mb-3">
          <label htmlFor="naechste">Nächste Prüfung empfohlen bis</label>
          <input
            id="naechste"
            type="date"
            value={naechste}
            onChange={(e) => setzeNaechste(e.target.value)}
          />
        </div>

        {konflikt && (
          <div className="mb-3 rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3">
            <p className="font-semibold text-stufe-2">{konflikt.meldung}</p>
            {konflikt.offene_platzkriterien.length > 0 && (
              <p className="mt-1 text-base">
                <span className="font-semibold">Platz:</span>{' '}
                {konflikt.offene_platzkriterien.join(', ')}
              </p>
            )}
            {konflikt.offene_geraete.length > 0 && (
              <p className="mt-1 text-base">
                <span className="font-semibold">Geräte:</span>{' '}
                {konflikt.offene_geraete.join(', ')}
              </p>
            )}
            <label className="mt-3 flex items-start gap-2 font-normal">
              <input
                type="checkbox"
                checked={offeneFestschreiben}
                onChange={(e) => setzeOffeneFestschreiben(e.target.checked)}
                className="mt-0.5"
                style={{ width: '24px', height: '24px', minHeight: '24px' }}
              />
              <span className="text-base">
                Diese {konflikt.punkte_offen} Punkte sind am Platz nicht vorhanden und werden
                als „--" festgeschrieben.
              </span>
            </label>
          </div>
        )}

        <Fehlerhinweis fehler={fehler} />
      </>
    </Blatt>
  )
}
